import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import store from './redux/store'; 
import { 
  loadSavedProjects, 
  saveProject, 
  exportProjectToJSON, 
  importProjectFromJSON,
  selectSavedProjects,
  selectProjectName,
  loadProject,
  selectMenus,
  setMenus
} from './redux/editorSlice';
import { 
  fetchWithcookieData, 
  saveMenuJsonToServer,
  checkAdminToken,
  checkLoginStatus,
  selectIsAdminLoggedIn,
  selectIsUserLoggedIn,
  RESTRICTED_PATHS,
  selectCompanyIsUse,
  selectGlobalMenus,
  selectMainMenus,
  loadMenus,
  fetchCartItems
} from './redux/WithcookieSlice';
import EditorCanvas from './components/EditorCanvas';
import ComponentLibrary from './components/ComponentLibrary';
import PropertyPanel from './components/propertyPanel/index.js';
import ProjectManager from './components/ProjectManager';
import Preview from './Preview';
import StateStop from './pages/StateStop';
import TossTest from './pages/TossTest';
import { CartComponent } from './components/module/Cart';
import { PaymentComponent } from './components/module/Payment';
import buildAndDownloadBuildFolder from './utils/buildAndDownloadBuildFolder';
import { previewCurrentMenu, previewCurrentMenuWithoutNewWindow } from './utils/PreviewUtils';
import './App.css';
import './styles/MenuStyles.css';

// 결제 관련 컴포넌트 임포트
import { PaymentSuccessComponent as PaymentSuccess } from './components/module/PaymentSuccess';
import { PaymentFailComponent as PaymentFail } from './components/module/PaymentFail';
import PayLetterSuccess from './components/module/PayLetterPaymentSuccess/PayLetterSuccess';
import PayLetterFail from './components/module/PayLetterPaymentFail/PayLetterFail';
import PayLetterReturn from './components/module/PayLetterPayment/PayLetterReturn';

// AppDispatch 타입 정의
type AppDispatch = typeof store.dispatch;

// 커스텀 useDispatch 훅
const useAppDispatch = () => useDispatch<AppDispatch>();

// 프로젝트 인터페이스 정의
interface Project {
  id: string;
  name: string;
  components: any[]; // 더 상세한 타입이 필요하면 확장할 수 있음
  timestamp?: string;
  canvasSize?: { width: number; height: number };
  layout?: any;
  menus?: any[]; // 메뉴 정보 추가
}

// 메뉴 인터페이스 정의
interface Menu {
  id: string;
  name: string;
  path: string;
  type: string;
  parentId: string | null;
  order: number;
  visible: boolean;
  components: string[];
  externalLink?: string;  // 외부 링크 필드 추가
  menuAction?: string;    // 메뉴 액션 필드 추가 (로그아웃 등)
  is_logged?: boolean;    // 로그인 유저에게만 표시
  is_not_logged?: boolean; // 로그아웃 유저에게만 표시
}

// 비밀번호 보호 컴포넌트
const PasswordProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isPasswordAuthenticated, setIsPasswordAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const [forceRender, setForceRender] = useState(0);
  const dispatch = useAppDispatch();
  const [adminCheckFailed, setAdminCheckFailed] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false); // 초기 상태를 false로 변경
  
  // 이전 경로를 저장하는 ref 추가
  const prevPathRef = useRef('');
  // 인증 완료 상태를 저장하는 ref 추가
  const authCompletedRef = useRef(false);
  // 마지막 인증 확인 시간
  const [lastAuthCheckTime, setLastAuthCheckTime] = useState(0);
  
  // API 호출 중복 방지 ref
  const checkingAuthRef = useRef(false);
  
  // 중복 호출 방지를 위한 인증 확인 함수
  const checkAuth = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // 이미 호출 중이면 대기
    if (checkingAuthRef.current) {
      console.log('PasswordProtected - 이미 인증 확인 중, 중복 호출 방지');
      return null;
    }
    
    // forceRefresh가 아닌 경우에만 시간 체크 (forceRefresh는 항상 허용)
    if (!forceRefresh && now - lastAuthCheckTime < 3000) {
      console.log('PasswordProtected - 최근 인증 확인됨, 중복 호출 방지');
      return null;
    }
    
    try {
      checkingAuthRef.current = true; // 호출 시작 표시
      console.log('PasswordProtected - checkAuth 호출:', { forceRefresh, lastCheck: lastAuthCheckTime });
      const result = await dispatch(checkAdminToken({forceRefresh})).unwrap();
      setLastAuthCheckTime(now);
      return result;
    } catch (error) {
      console.error('PasswordProtected - 인증 확인 오류:', error);
      return null;
    } finally {
      checkingAuthRef.current = false; // 호출 완료 표시
    }
  }, [dispatch, lastAuthCheckTime]);
  
  // 컴포넌트 마운트 시 즉시 어드민 로그인 상태 확인
  useEffect(() => {
    // 새로고침이나 첫 로드 시에는 무조건 확인
    console.log('App useEffect - checkAdminToken 호출 시도');
    
    // authCompletedRef 조건 제거하여 새로고침 시에도 확인하도록 수정

    const initialCheckAdminLoginStatus = async () => {
      // 새로고침 후에는 무조건 한 번 확인하도록 수정
      // isCheckingAdmin 조건 제거
      
      try {
        setIsCheckingAdmin(true); // 체크 시작
        setError('관리자 로그인 상태를 확인 중입니다...');
        
        // 공통 인증 확인 함수 사용
        const result = await checkAuth(true);
        if (!result) return;
        
        
        // Redux 상태 또는 API 응답 중 하나라도 로그인 상태면 접근 허용
        if (result.isLoggedIn || result.adminUser || isAdminLoggedIn) {
          setAdminCheckFailed(false);
          setError('');
          
          // 상태 업데이트 확인을 위해 추가 - 관리자 사용자가 있으면 명시적으로 Redux 상태 강제 업데이트
          if (result.adminUser) {
            dispatch({ 
              type: 'withcookie/setAdminUser', 
              payload: result.adminUser 
            });
          }

          // 최초 접속 시에도 모달을 닫도록 인증 상태 설정
          setTimeout(() => {
            // 인증 완료 상태를 먼저 설정하여 추가 체크 방지
            authCompletedRef.current = true;
            setIsPasswordAuthenticated(true);
          }, 500);
          
        } else {
          setAdminCheckFailed(true);
          setError('관리자 로그인이 필요합니다. 비밀번호를 입력하여 접속하세요.');
        }
      } catch (error) {
        console.error('어드민 로그인 상태 확인 중 오류:', error);
        
        // 오류가 발생했더라도 이미 Redux 상태가 로그인 상태면 접근 허용
        if (isAdminLoggedIn) {
          setAdminCheckFailed(false);
          setError('');
          // 오류 발생해도 Redux 상태가 로그인 상태면 모달 닫기
          authCompletedRef.current = true;
          setIsPasswordAuthenticated(true);
        } else {
          setAdminCheckFailed(true);
          setError('관리자 확인 중 오류가 발생했습니다. 비밀번호를 입력하여 접속하세요.');
        }
      } finally {
        setIsCheckingAdmin(false); // 체크 완료
      }
    };
    
    // 현재 경로가 /edit으로 시작하는지 확인
    const isEditPage = location.pathname.startsWith('/edit');
    
    // 초기 마운트시에만 관리자 상태 확인 (한 번만 실행)
    if (prevPathRef.current === '') {
      initialCheckAdminLoginStatus();
    }
    
    // 현재 경로 저장
    prevPathRef.current = location.pathname;
  }, [dispatch, location.pathname]);
  
  // isAdminLoggedIn 상태 변화 감지
  useEffect(() => {
    // 관리자 로그인 상태가 true로 변경되면 오류 메시지 제거 및 인증 완료 처리
    if (isAdminLoggedIn && !authCompletedRef.current) {
      setError('');
      setAdminCheckFailed(false);
      authCompletedRef.current = true;
      setIsPasswordAuthenticated(true);
    }
  }, [isAdminLoggedIn]);
  
  // 현재 경로가 /edit으로 시작하는지 확인
  const isEditPage = location.pathname.startsWith('/edit');
  
  // 비밀번호 인증 처리
  const handleAuthenticate = () => {
    if (password === 'rhksflqlqjs@1' || password === '관리비번@1') {  // 'rhksflqlqjs@1' 비밀번호 확인
      authCompletedRef.current = true;
      setIsPasswordAuthenticated(true);
      setError('');
    } else {
      setError('비밀번호가 올바르지 않습니다.');
    }
  };
  
  // 키 이벤트 처리
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAuthenticate();
    }
  };
  
  // 관리자 상태 수동 확인
  const checkAdminStatus = async () => {
    try {
      setError('관리자 상태 확인 중...');
      setIsCheckingAdmin(true); // 체크 시작
      
      const result = await checkAuth(true);
      if (!result) return;
      
      // 응답 결과에 따라 처리
      if (result.isLoggedIn || result.adminUser) {
        
        // 명시적으로 리덕스 상태 업데이트 (중요)
        if (result.adminUser) {
          dispatch({ 
            type: 'withcookie/setAdminUser', 
            payload: result.adminUser 
          });
        }
        
        setError('관리자 로그인이 확인되었습니다.');
        setAdminCheckFailed(false);
        
        // 관리자 확인 후 1초 후에 모달창 닫기 위해 상태 명시적 설정
        setTimeout(() => {
          // 인증 완료 상태를 먼저 설정
          authCompletedRef.current = true;
          // 관리자 로그인 상태를 true로 강제 설정
          setIsPasswordAuthenticated(true); 
          setIsCheckingAdmin(false);
        }, 1000);
      } else {
        setError('관리자 로그인이 확인되지 않았습니다. 비밀번호를 입력하여 접속하세요.');
        setAdminCheckFailed(true);
        setIsCheckingAdmin(false);
      }
    } catch (error) {
      console.error('[수동 관리자 확인] 오류:', error);
      setError('관리자 확인 중 오류가 발생했습니다. 비밀번호를 입력하여 접속하세요.');
      setAdminCheckFailed(true);
      setIsCheckingAdmin(false);
    }
  };
  
  // 로딩 중 화면 표시
  if (isCheckingAdmin && isEditPage) {
    return (
      <div className="password-modal-overlay">
        <div className="password-modal">
          <h2>관리자 확인 중</h2>
          <p>관리자 로그인 상태를 확인하고 있습니다...</p>
          <div className="loading-spinner" style={{ textAlign: 'center', margin: '20px 0' }}>
            <div style={{ width: '30px', height: '30px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }
  
  // 접근이 허용되지 않은 경우 비밀번호 입력 모달 표시
  if (!(isAdminLoggedIn || isPasswordAuthenticated || !isEditPage)) {
    return (
      <div className="password-modal-overlay">
        <div className="password-modal">
          <h2>페이지 접근</h2>
          <p>이 페이지는 관리자 또는 비밀번호로 보호되어 있습니다.</p>
          <div className="password-input-container">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="비밀번호를 입력하세요"
              className={error ? 'error' : ''}
              autoFocus
            />
            <button onClick={handleAuthenticate}>확인</button>
          </div>
          {error && <p className="error-message">{error}</p>}
          <div className="admin-check-section">
            <p>이미 관리자로 로그인되어 있습니까?</p>
            <button onClick={checkAdminStatus} className="check-admin-button">관리자 상태 확인</button>
          </div>
          {adminCheckFailed && (
            <p className="warn-message">
              관리자 API 접근이 실패했습니다. 비밀번호를 입력하여 접속하세요.
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // 인증 완료 또는 인증이 필요 없는 경로인 경우 자식 컴포넌트 렌더링
  return <>{children}</>;
};

// 편집 모드 라우트 컴포넌트
const EditRoute = () => {
  const params = useParams<{ menuPath?: string; ['*']?: string }>();
  const menuPath = params.menuPath;
  const restPath = params['*']; // 계층적 라우팅 경로의 나머지 부분
  
  const dispatch = useAppDispatch();
  const menus = useSelector(selectMenus);
  const navigate = useNavigate();
  const location = useLocation();
  const companyIsUse = useSelector(selectCompanyIsUse);
  
  // 이전 경로를 저장하는 ref 추가
  const prevPathRef = useRef(location.pathname);
  
  useEffect(() => {
    // 경로가 실제로 변경된 경우에만 실행
    if (prevPathRef.current === location.pathname) {
      return;
    }
    
    // 현재 경로 업데이트
    prevPathRef.current = location.pathname;

    // 금지된 경로인지 확인 (필요한 경우에만 API 호출)
    const currentPath = location.pathname;
    if (RESTRICTED_PATHS.includes('/state-stop') && currentPath !== '/state-stop' && !companyIsUse) {
      // companyIsUse가 false인 경우에만 API 호출
      dispatch(fetchWithcookieData());
    }
    
    // URL 경로 구성
    let fullPath = menuPath ? `/${menuPath}` : '/';
    if (restPath) {
      fullPath += `/${restPath}`;
    }
    
    // URL 경로와 일치하는 메뉴 찾기 (정확한 경로 일치)
    let matchedMenu = menus.find((menu: Menu) => menu.path === fullPath);
    
    // 동적 라우팅 매개변수가 있는 메뉴 처리
    if (!matchedMenu && fullPath !== '/') {
      // 동적 라우팅 패턴 검색
      for (const menu of menus) {
        const menuPathPattern = menu.path;
        
        // product/:id 같은 패턴 검색
        if (menuPathPattern.includes('/:')) {
          const menuPathParts = menuPathPattern.split('/').filter(Boolean);
          const fullPathParts = fullPath.split('/').filter(Boolean);
          
          // 경로 길이가 같을 경우에만 매치 시도
          if (menuPathParts.length === fullPathParts.length) {
            let isMatch = true;
            
            for (let i = 0; i < menuPathParts.length; i++) {
              const menuPart = menuPathParts[i];
              const currentPart = fullPathParts[i];
              
              if (menuPart.startsWith(':')) {
                // 매개변수 부분 - 값 추출 (이 값은 편집기에서 사용하지 않으므로 추출만 함)
                continue;
              } else if (menuPart !== currentPart) {
                // 일반 경로 부분 - 정확히 일치해야 함
                isMatch = false;
                break;
              }
            }
            
            if (isMatch) {
              matchedMenu = menu;
              break;
            }
          }
        }
      }
    }
    
    // 일치하는 메뉴가 없으면 루트 경로로 이동 (fullPath가 '/'가 아닌 경우에만)
    if (!matchedMenu && fullPath !== '/') {
      navigate('/edit');
    }
  }, [dispatch, menuPath, restPath, menus, navigate, location.pathname, companyIsUse]);
  
  // 회사 사용 권한이 비활성화되어 있으면 null 반환 (아무것도 렌더링하지 않음)
  if (!companyIsUse && location.pathname !== '/state-stop') {
    return null;
  }
  
  return (
    <div className="app-main">
      <ComponentLibrary />
      <EditorCanvas menuPath={menuPath ? `/${menuPath}${restPath ? `/${restPath}` : ''}` : '/'} />
      <PropertyPanel />
    </div>
  );
};

function AppContent() {
  const dispatch = useAppDispatch();
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const savedProjects = useSelector(selectSavedProjects) as Project[];
  const projectName = useSelector(selectProjectName);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);  // 데이터 로드 완료 상태
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const menus = useSelector(selectMenus);
  const globalMenusFromWithcookie = useSelector(selectGlobalMenus);
  const mainMenusFromWithcookie = useSelector(selectMainMenus);
  const location = useLocation();
  const isUserLoggedInValue = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedInValue = useSelector(selectIsAdminLoggedIn);
  
  // 현재 경로가 /edit으로 시작하는지 확인
  const isEditPage = location.pathname.startsWith('/edit');

  // 이전 경로를 저장하는 ref 추가
  const prevPathRef = useRef(location.pathname);

  // 메뉴 타입 선택 상태 추가
  const [selectedMenuType, setSelectedMenuType] = useState<string>('GLOBAL'); // 기본값을 GLOBAL로 설정
  
  // 메뉴 데이터 변경 추적을 위한 ref
  const prevGlobalMenusRef = useRef<any[]>([]);
  const prevMainMenusRef = useRef<any[]>([]);
  const syncInProgressRef = useRef<boolean>(false);

  // 메뉴의 깊은 비교를 위한 함수 - 배열의 길이와 ID만 비교
  const areMenusChanged = (prevMenus: any[], newMenus: any[]) => {
    if (prevMenus.length !== newMenus.length) return true;
    
    // ID 집합으로 변환하여 비교
    const prevIds = new Set(prevMenus.map(menu => menu.id));
    const newIds = new Set(newMenus.map(menu => menu.id));
    
    if (prevIds.size !== newIds.size) return true;
    
    // 모든 ID가 이전과 동일한지 확인 (Array.from으로 Set을 배열로 변환)
    for (const id of Array.from(newIds)) {
      if (!prevIds.has(id)) return true;
    }
    
    return false;
  };

  // Deploy 기능 핸들러 (useCallback으로 메모이제이션)
  const adjustToServer = useCallback(async () => {
    try {
      // 먼저 저장 기능 실행
      dispatch(saveProject());

      // 미리보기 데이터 준비 (새 창을 열지 않고)
      previewCurrentMenuWithoutNewWindow();

      // 현재 상태에서 필요한 데이터 준비
      const currentState = store.getState();

      console.log(currentState);
      
      // 글로벌 메뉴와 주 메뉴 추출
      const globalMenus = currentState.editor.menus.filter((menu: any) => menu.type === 'GLOBAL');
      const mainMenus = currentState.editor.menus.filter((menu: any) => menu.type === 'MAIN');
      
      const previewData = {
        components: currentState.editor.components,
        menus: currentState.editor.menus,
        layoutInfo: currentState.editor.layout,
        timestamp: new Date().toISOString()
      };

      const data = {
        previewData: JSON.stringify(previewData),
        previewPath: '/',
        savedProjects: JSON.stringify(currentState.editor.savedProjects),
        previewTimestamp: new Date().toISOString(),
        // 메뉴 데이터 명시적 추가
        globalMenus: JSON.stringify(globalMenus),
        mainMenus: JSON.stringify(mainMenus)
      };

      // withcookieSlice의 saveMenuJsonToServer 액션 디스패치
      const result = await dispatch(saveMenuJsonToServer(data)).unwrap();
      
      alert('✅ 서버에 성공적으로 배포했습니다!');
    } catch (error: any) {
      console.error('서버 저장 실패:', error);
      alert('❌ 서버 저장 실패: ' + (error.message || '알 수 없는 오류'));
    }
  }, [dispatch]); // dispatch만 의존성으로 추가
  
  // 저장 기능 핸들러
  const handleSave = () => {
    dispatch(saveProject());
    alert('프로젝트가 저장되었습니다!');
  };

  // 불러오기 기능 핸들러
  const handleLoad = () => {
    setShowProjectSelector(true);
  };

  // 불러오기 선택 핸들러
  const handleLoadProject = (projectId: string) => {
    dispatch(loadProject(projectId));
    setShowProjectSelector(false);
  };

  // 내보내기 기능 핸들러
  const handleExport = () => {
    dispatch(exportProjectToJSON());
  };

  // 가져오기 기능 핸들러
  const handleImport = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 파일 선택 핸들러
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const projectData = JSON.parse(event.target?.result as string);
        dispatch(importProjectFromJSON(projectData));
        alert('프로젝트를 성공적으로 가져왔습니다.');
      } catch (error) {
        console.error('JSON 파일 파싱 오류:', error);
        alert('유효하지 않은 프로젝트 파일입니다.');
      }
    };
    reader.readAsText(file);
    
    // 파일 입력 초기화 (같은 파일을 다시 선택할 수 있도록)
    e.target.value = '';
  };

  // 메뉴 타입 변경 핸들러
  const handleMenuTypeChange = (menuType: string) => {
    setSelectedMenuType(menuType);
  };
  
  // 빌드 다운로드 기능 핸들러
  const handleBuild = async () => {
    try {
      const result = await buildAndDownloadBuildFolder();
      
      if (result.success) {
        alert(result.message || '🚀 Deploy 완료! Deploy 폴더가 다운로드됩니다.');
      } else {
        alert('❌ Deploy 실패: ' + (result.error || '알 수 없는 오류'));
      }
    } catch (error: any) {
      console.error('Deploy 실패:', error);
      alert('❌ Deploy 실패: ' + (error.message || '알 수 없는 오류'));
    }
  };

  // 미리보기 기능 핸들러
  const handlePreview = () => {
    previewCurrentMenu();
  };

  // ============= React Hooks ================

  // PC 설정을 모바일에 적용하는 함수 추가
  const applyPcSettingsToMobile = useCallback(() => {
    try {
      // Redux 상태에서 현재 선택된 컴포넌트와 설정 가져오기
      const currentState = store.getState();
      const selectedComponentId = currentState.editor.selectedComponentId;
      
      if (!selectedComponentId) {
        alert('선택된 컴포넌트가 없습니다.');
        return;
      }
      
      // 액션 디스패치 - 편집기의 액션을 임포트하고 사용해야 함
      dispatch({ 
        type: 'editor/applyPcSettingsToMobile', 
        payload: { componentId: selectedComponentId } 
      });
      
      alert('PC 설정이 모바일에 성공적으로 적용되었습니다.');
    } catch (error: any) {
      console.error('PC 설정을 모바일에 적용하는 중 오류 발생:', error);
      alert('❌ PC 설정을 모바일에 적용하는 중 오류 발생: ' + (error.message || '알 수 없는 오류'));
    }
  }, [dispatch]); // dispatch만 의존성으로 추가

  // Cmd+S / Ctrl+S 단축키 처리를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+S(Mac) 또는 Ctrl+S(Windows/Linux) 감지
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // 브라우저 기본 저장 동작 방지
        
        // 편집 페이지일 때만 동작
        if (isEditPage) {
          // 배포 확인 창 표시
          const confirmDeploy = window.confirm('서버에 배포하시겠습니까?');
          
          // 사용자가 확인을 눌렀을 때만 배포 실행
          if (confirmDeploy) {
            adjustToServer();
          }
        }
      }
    };
    
    // 이벤트 리스너 등록
    window.addEventListener('keydown', handleKeyDown);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditPage, adjustToServer]); // adjustToServer 의존성 추가
  
  // 경로 변경 감지 및 처리
  useEffect(() => {
    // 이전 경로와 현재 경로가 같으면 중복 처리 방지
    if (prevPathRef.current === location.pathname) {
      return;
    }
    
    // 현재 경로 저장
    prevPathRef.current = location.pathname;
    
    // 에디터(/edit/) 경로나 state-stop 페이지에서는 리다이렉트 처리 중단
    if (location.pathname.startsWith('/edit/') || location.pathname === '/edit' || 
        location.pathname === '/state-stop') {
      return;
    }

    // 로그인 상태 확인
    const isLoggedIn = isUserLoggedInValue || isAdminLoggedInValue;
    
    // 현재 경로와 일치하는 메뉴 찾기
    const currentPath = location.pathname;
    
    // 모든 메뉴 목록 가져오기 (전역 편집기 메뉴 + 서버에서 가져온 메뉴)
    const allMenus = menus;
    
    // 현재 경로와 일치하는 메뉴 찾기 (정확한 경로 일치)
    let currentMenu = allMenus.find((menu: Menu) => menu.path === currentPath);
    
    // 가변 경로 처리 (예: product/:id) - 매치되지 않은 경우만 실행
    if (!currentMenu) {
      for (const menu of allMenus) {
        if (menu.path && menu.path.includes('/:')) {
          const menuPathPattern = menu.path;
          const menuPathParts = menuPathPattern.split('/').filter(Boolean);
          const currentPathParts = currentPath.split('/').filter(Boolean);
          
          // 빠른 길이 확인 - 길이가 다르면 계속 진행
          if (menuPathParts.length !== currentPathParts.length) {
            continue;
          }
          
          let isMatch = true;
          
          for (let i = 0; i < menuPathParts.length; i++) {
            const menuPart = menuPathParts[i];
            const currentPart = currentPathParts[i];
            
            if (menuPart.startsWith(':')) {
              // 매개변수 부분 - 계속 진행
              continue;
            } else if (menuPart !== currentPart) {
              // 일반 경로 부분 - 정확히 일치해야 함
              isMatch = false;
              break;
            }
          }
          
          if (isMatch) {
            currentMenu = menu as Menu;
            break;
          }
        }
      }
    }
    
    // 메뉴를 찾았고 로그인 조건이 있는 경우만 처리
    if (currentMenu) {
      // 로그아웃 시에만 표시(is_not_logged)가 설정된 메뉴에 로그인 상태로 접근한 경우
      if (currentMenu.is_not_logged && isLoggedIn) {
        navigate('/');
        return;
      }
      
      // 로그인 시에만 표시(is_logged)가 설정된 메뉴에 비로그인 상태로 접근한 경우
      if (currentMenu.is_logged && !isLoggedIn) {
        navigate('/');
        return;
      }
    }
  }, [location.pathname, isUserLoggedInValue, isAdminLoggedInValue, menus, navigate]);

  useEffect(() => {
    dispatch(loadSavedProjects());
  }, [dispatch]);

  // 메뉴 데이터 로드
  useEffect(() => {
    if (isEditPage) {
      dispatch(loadMenus());
    }
  }, [dispatch, isEditPage]);

  // withcookie 슬라이스에서 불러온 메뉴 정보를 에디터 상태에 반영
  useEffect(() => {
    // 이미 동기화 진행 중이면 중복 실행 방지
    if (syncInProgressRef.current) {
      return;
    }
    
    // 데이터가 로드된 경우에만 처리
    if (globalMenusFromWithcookie.length > 0 || mainMenusFromWithcookie.length > 0) {
      // 변경 사항 확인 (ID 집합만 비교하여 간단하게 처리)
      const globalMenusChanged = areMenusChanged(prevGlobalMenusRef.current, globalMenusFromWithcookie);
      const mainMenusChanged = areMenusChanged(prevMainMenusRef.current, mainMenusFromWithcookie);
      
      // 변경 사항이 없으면 업데이트 생략
      if (!globalMenusChanged && !mainMenusChanged) {
        return;
      }
      
      
      // 동기화 중 플래그 설정
      syncInProgressRef.current = true;
      
      try {
        // 에디터 상태의 다른 메뉴 보존
        const otherMenus = menus.filter((menu: Menu) => 
          menu.type !== 'GLOBAL' && menu.type !== 'MAIN'
        );
        
        // 에디터 메뉴 상태 업데이트
        dispatch(setMenus([
          ...globalMenusFromWithcookie,
          ...mainMenusFromWithcookie,
          ...otherMenus
        ]));
        
        // 현재 메뉴 상태 저장
        prevGlobalMenusRef.current = [...globalMenusFromWithcookie];
        prevMainMenusRef.current = [...mainMenusFromWithcookie];
      } finally {
        // 동기화 완료 플래그 해제
        syncInProgressRef.current = false;
      }
    }
  }, [globalMenusFromWithcookie, mainMenusFromWithcookie, dispatch, menus]);

  // savedProjects가 있을 때 자동으로 첫 번째 프로젝트 불러오기
  useEffect(() => {
    if (savedProjects.length > 0 && isEditPage) {
      dispatch(loadProject(savedProjects[0].id));
    }
  }, [savedProjects, dispatch, isEditPage]);

  // Withcookie 데이터 초기 로드
  useEffect(() => {
    // 이미 데이터가 로드되었으면 중복 호출 방지
    if (dataLoaded) {
      return;
    }
    
    const loadData = async () => {
      try {
        // 기본 타이틀 설정
        document.title = "사이트 디자인 편집";
        
        // API 데이터 가져오기
        const data = await dispatch(fetchWithcookieData());
        
        // 회사 이름이 있으면 타이틀 업데이트
        if (data?.skin?.company?.name) {
          document.title = `${data.skin.company.name} 디자인 편집`;
        }
        
        // favicon_ico_url이 있으면 파비콘 업데이트
        if (data?.skin?.theme?.favicon_ico_url) {
          // 기존 favicon 링크 요소 찾기
          const existingFavicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
          
          if (existingFavicon) {
            // 기존 favicon이 있으면 href 속성만 변경
            existingFavicon.href = data.skin.theme.favicon_ico_url;
          } else {
            // 기존 favicon이 없으면 새로 생성하여 추가
            const newFavicon = document.createElement("link");
            newFavicon.rel = "icon";
            newFavicon.href = data.skin.theme.favicon_ico_url;
            document.head.appendChild(newFavicon);
          }
        }
        
        // favicon16x_url이 있으면 16x16 파비콘 업데이트
        if (data?.skin?.theme?.favicon16x_url) {
          // 기존 16x16 favicon 링크 요소 찾기
          const existingFavicon16 = document.querySelector("link[rel='icon'][sizes='16x16']") as HTMLLinkElement;
          
          if (existingFavicon16) {
            existingFavicon16.href = data.skin.theme.favicon16x_url;
          } else {
            // 새로운 파비콘 요소를 생성하여 속성을 설정합니다
            const newFavicon16 = document.createElement("link");
            newFavicon16.rel = "icon";
            newFavicon16.href = data.skin.theme.favicon16x_url;
            newFavicon16.setAttribute('sizes', '16x16');
            newFavicon16.type = "image/png";
            document.head.appendChild(newFavicon16);
          }
        }
        
        // favicon32x_url이 있으면 32x32 파비콘 업데이트
        if (data?.skin?.theme?.favicon32x_url) {
          // 기존 32x32 favicon 링크 요소 찾기
          const existingFavicon32 = document.querySelector("link[rel='icon'][sizes='32x32']") as HTMLLinkElement;
          
          if (existingFavicon32) {
            existingFavicon32.href = data.skin.theme.favicon32x_url;
          } else {
            // 새로운 파비콘 요소를 생성하여 속성을 설정합니다
            const newFavicon32 = document.createElement("link");
            newFavicon32.rel = "icon";
            newFavicon32.href = data.skin.theme.favicon32x_url;
            newFavicon32.setAttribute('sizes', '32x32');
            newFavicon32.type = "image/png";
            document.head.appendChild(newFavicon32);
          }
        }
        
        // 사이트 검증 메타 태그 업데이트
        if (data?.skin?.siteVerificationGoogle || data?.skin?.siteVerificationNaver) {
          const { updateSiteVerificationMetaTags } = await import('./utils/metaTagUtils');
          updateSiteVerificationMetaTags(
            data.skin.siteVerificationGoogle,
            data.skin.siteVerificationNaver
          );
        }
        
        // 데이터 로드 완료 표시
        setDataLoaded(true);
      } catch (error) {
        console.error('Withcookie 데이터 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, dataLoaded]); // dataLoaded 의존성 추가

  // 로딩 중일 때 로딩 표시
  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p>데이터를 불러오는 중입니다...</p>
      </div>
    );
  }

  // 네비게이션 메뉴 렌더링
  const renderNavigationMenu = () => {
    // 글로벌 메뉴와 주 메뉴를 필터링하고 순서대로 정렬
    const globalMenus = menus
      .filter((menu: Menu) => menu.type === 'GLOBAL' && !menu.parentId && menu.visible)
      .sort((a: Menu, b: Menu) => (a.order || 0) - (b.order || 0));
    
    const mainMenus = menus
      .filter((menu: Menu) => menu.type === 'MAIN' && !menu.parentId && menu.visible)
      .sort((a: Menu, b: Menu) => (a.order || 0) - (b.order || 0));
    
    if (globalMenus.length === 0 && mainMenus.length === 0) return null;
    
    return (
      <div className="navigation-menu">
        {/* 메뉴 타입 선택기 */}
        <div className="menu-type-selector" style={{ position: 'absolute', left: '0' }}>
          <div 
            className={`menu-type-option ${selectedMenuType === 'GLOBAL' ? 'active' : ''}`}
            onClick={() => handleMenuTypeChange('GLOBAL')}
          >
            <span className="menu-type-icon">🌐</span>
            <span className="menu-type-label">글로벌 메뉴</span>
          </div>
          <div 
            className={`menu-type-option ${selectedMenuType === 'MAIN' ? 'active' : ''}`}
            onClick={() => handleMenuTypeChange('MAIN')}
          >
            <span className="menu-type-icon">📂</span>
            <span className="menu-type-label">주 메뉴</span>
          </div>
        </div>
        
        {/* 메뉴 표시 영역 - 선택된 메뉴 타입에 따라 표시 */}
        <div className="menu-display-area" style={{ margin: '0 auto', textAlign: 'center' }}>
          {/* 글로벌 메뉴 (글로벌 메뉴가 선택된 경우에만 표시) */}
          {selectedMenuType === 'GLOBAL' && (
            <div className="menu-section">
              {globalMenus.length > 0 ? (
                <ul className="nav-menu-list global-menu">
                  {globalMenus.map((menu: Menu) => (
                    <li key={menu.id} className="nav-menu-item">
                      <a 
                        href="#"
                        className={`nav-menu-link ${location.pathname === `/edit${menu.path}` ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // 현재 경로와 다를 때만 navigate 함수 호출
                          if (location.pathname !== `/edit${menu.path}`) {
                            navigate(`/edit${menu.path}`);
                          }
                        }}
                      >
                        {menu.name}
                      </a>
                      {renderSubMenu(menu.id)}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-menu-message">
                  글로벌 메뉴가 없습니다. 메뉴 관리에서 추가해주세요.
                </div>
              )}
            </div>
          )}
          
          {/* 주 메뉴 (주 메뉴가 선택된 경우에만 표시) */}
          {selectedMenuType === 'MAIN' && (
            <div className="menu-section">
              <ul className="nav-menu-list main-menu">
               
                {mainMenus.map((menu: Menu) => (
                  <li key={menu.id} className="nav-menu-item">
                    <a 
                      href="#"
                      className={`nav-menu-link ${location.pathname === `/edit${menu.path}` ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        // 현재 경로와 다를 때만 navigate 함수 호출
                        if (location.pathname !== `/edit${menu.path}`) {
                          navigate(`/edit${menu.path}`);
                        }
                      }}
                    >
                      {menu.name}
                    </a>
                    {renderSubMenu(menu.id)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // 하위 메뉴 렌더링
  const renderSubMenu = (parentId: string) => {
    const subMenus = menus
      .filter((menu: Menu) => menu.parentId === parentId && menu.visible)
      .sort((a: Menu, b: Menu) => (a.order || 0) - (b.order || 0));
    
    if (subMenus.length === 0) return null;
    
    return (
      <ul className="sub-nav-menu-list">
        {subMenus.map((menu: Menu) => (
          <li key={menu.id} className="sub-nav-menu-item">
            <a 
              href="#"
              className={`sub-nav-menu-link ${location.pathname === `/edit${menu.path}` ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                // 현재 경로와 다를 때만 navigate 함수 호출
                if (location.pathname !== `/edit${menu.path}`) {
                  navigate(`/edit${menu.path}`);
                }
              }}
            >
              {menu.name}
            </a>
            {renderSubMenu(menu.id)} {/* 3단계까지 재귀적으로 렌더링 */}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {/* 편집 페이지일 때만 헤더를 표시 */}
      {isEditPage && (
        <header className="app-header">
          <div className="header-logo">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 6L8 12L14 18" stroke="#4361ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18 6L12 12L18 18" stroke="#4361ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h1>웹 빌더</h1>
          </div>
          <div className="header-nav">
            {renderNavigationMenu()}
          </div>
          <div className="header-actions">
            <button className="primary build-button" onClick={adjustToServer}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 4V16L12 14L8 16V4M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Deploy to Server
            </button>
            
            {/* PC 설정을 모바일에 적용하는 버튼 추가 */}
            <button className="secondary mobile-sync-button" onClick={applyPcSettingsToMobile}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6H20M4 18H20M8 6V18M16 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              PC → 모바일 적용
            </button>
          </div>
        </header>
      )}

      {/* 프로젝트 선택기 모달 */}
      {showProjectSelector && (
        <div className="modal-overlay" onClick={() => setShowProjectSelector(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>프로젝트 불러오기</h3>
              <button className="modal-close" onClick={() => setShowProjectSelector(false)}>×</button>
            </div>
            
            {savedProjects.length > 0 ? (
              <div className="project-list">
                {savedProjects.map((project: Project) => (
                  <div key={project.id} className="project-item">
                    <div className="project-item-name">{project.name}</div>
                    <div className="project-item-actions">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => handleLoadProject(project.id)}
                      >
                        불러오기
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>저장된 프로젝트가 없습니다.</p>
            )}
          </div>
        </div>
      )}
      
      {/* 파일 입력(숨김) */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json"
        onChange={handleFileChange}
      />
    </>
  );
}

// 프로젝트 메인 컴포넌트
function App() {
  return (
    <DndProvider backend={HTML5Backend}>
      <BrowserRouter>
        <AuthProvider>
          <PasswordProtectedRoute>
            <Routes>
              {/* Home - Preview 컴포넌트를 직접 렌더링 */}
              <Route path="/" element={<Preview />} />
              
              {/* 시스템 접근 제한 페이지 */}
              <Route path="/state-stop" element={<StateStop />} />

              {/* 토스 테스트 페이지 */}
              <Route path="/toss-test" element={<TossTest />} />
              
              {/* 에디터 관련 경로 - EditRoute를 직접 사용 */}
              {/* Editor Routes */}
              <Route path="/edit" element={
                <>
                  <AppContent />
                  <EditRoute />
                </>
              } />
              
              <Route path="/edit/:menuPath/*" element={
                <>
                  <AppContent />
                  <EditRoute />
                </>
              } />
              
              {/* 프리뷰 관련 경로 */}
              <Route path="/preview/*" element={<Preview />} />
              
              {/* 🚀 Finapp 관련 경로 추가 */}
              <Route path="/finapp/*" element={<Preview />} />
              
              {/* 🚀 Gpc 관련 경로 추가 */}
              <Route path="/gpc/*" element={<Preview />} />

              {/* 장바구니 경로 - Preview 컴포넌트를 통해 헤더/푸터 포함 */}
              <Route path="/cart" element={<Preview cartPage={true} />} />
              
              {/* 결제 관련 경로 추가 */}
              <Route path="/payment/success" element={<Preview paymentSuccessPage={true} />} />
              <Route path="/payment/fail" element={<Preview paymentFailPage={true} />} />
              
              {/* PayLetter 결제 관련 경로 추가 - 백엔드 URL 구조에 맞게 수정 */}
              <Route path="/payment/payletter/return" element={<PayLetterReturn />} />
              <Route path="/payment-success" element={<PayLetterSuccess />} />
              <Route path="/payment-fail" element={<PayLetterFail />} />
              
              {/* 상품 상세 페이지 경로 추가 */}
              <Route path="/products/:id" element={<Preview productDetailPage={true} />} />
              
              {/* robots.txt 파일 제공 */}
              <Route path="/robots.txt" element={
                <div style={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}>
                  {`# https://www.robotstxt.org/robotstxt.html
User-agent: *
Disallow:`}
                </div>
              } />
              
              {/* 동적 경로들도 Preview 컴포넌트에서 처리하도록 설정 */}
              <Route path="/:menuPath/*" element={<Preview />} />
              
              {/* 기본 리디렉션 - 찾을 수 없는 경로는 루트로 */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </PasswordProtectedRoute>
        </AuthProvider>
      </BrowserRouter>
    </DndProvider>
  );
}

// 인증 상태를 확인하는 컴포넌트
const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const [authInitialized, setAuthInitialized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const companyIsUse = useSelector(selectCompanyIsUse);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const [lastAuthCheckTime, setLastAuthCheckTime] = useState(0); // 마지막 인증 확인 시간
  
  // API 호출 중복 방지 ref
  const mainCheckingAuthRef = useRef(false);
  
  // 전역 인증 상태 확인 함수 - 적절한 중복 호출 방지
  const checkAuth = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // 이미 호출 중이면 대기
    if (mainCheckingAuthRef.current) {
      console.log('Main - 이미 인증 확인 중, 중복 호출 방지');
      return null;
    }
    
    // forceRefresh가 아닌 경우에만 시간 체크 (forceRefresh는 항상 허용)
    if (!forceRefresh && now - lastAuthCheckTime < 3000) {
      console.log('Main - 최근 인증 확인됨, 중복 호출 방지');
      return null;
    }
    
    try {
      mainCheckingAuthRef.current = true; // 호출 시작 표시
      console.log('Main - checkAuth 호출:', { forceRefresh, lastCheck: lastAuthCheckTime });
      const result = await dispatch(checkAdminToken({forceRefresh})).unwrap();
      setLastAuthCheckTime(now); // 마지막 체크 시간 업데이트
      return result;
    } catch (error) {
      console.error('Main - 인증 확인 오류:', error);
      return null;
    } finally {
      mainCheckingAuthRef.current = false; // 호출 완료 표시
    }
  }, [dispatch, lastAuthCheckTime]);

  const menus = useSelector(selectMenus); // 메뉴 정보 가져오기
  const globalMenus = useSelector(selectGlobalMenus); // 글로벌 메뉴 정보 가져오기
  const mainMenus = useSelector(selectMainMenus); // 메인 메뉴 정보 가져오기
  
  // 이전 경로를 저장하는 ref 추가
  const prevPathRef = useRef(location.pathname);

  // 로그인 상태가 변경될 때 장바구니 아이템 로드
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      dispatch(fetchCartItems());
    }
  }, [isUserLoggedIn, isAdminLoggedIn, dispatch]);
  
  // 앱이 처음 로드될 때 한 번만 로그인 상태 확인
  useEffect(() => {
    // 이미 인증 상태를 확인했다면 중복 확인 방지
    if (authInitialized) return;

    // state-stop 페이지에서는 인증 확인 및 데이터 로드 중단
    if (location.pathname === '/state-stop') {
      setAuthInitialized(true);
      return;
    }

    const initializeAuth = async () => {
      try {
        // 어드민 상태 확인 시 오류가 발생해도 계속 진행
        try {
          // 어드민 상태 확인 (쿠키 기반)
          await checkAuth(true); // 초기화 시에는 강제 갱신
        } catch (error) {
          console.error('어드민 상태 확인 실패, 계속 진행합니다:', error);
        }
        
        // 사용자 상태 확인
        try {
          await dispatch(checkLoginStatus({}));
        } catch (error) {
          console.error('사용자 상태 확인 실패, 계속 진행합니다:', error);
        }
        
        // Withcookie 데이터 확인 (회사 상태 포함)
        try {
          await dispatch(fetchWithcookieData());
        } catch (error) {
          console.error('Withcookie 데이터 확인 실패, 계속 진행합니다:', error);
        }
        
        setAuthInitialized(true);
        
        // 첫 번째 확인 후 10초 후에 다시 한 번 확인 (초기화 문제 대비)
        // 10초마다 새로고침 문제 해결을 위해 임시 비활성화
        /*
        setTimeout(() => {
          // state-stop 페이지에서는 재확인 중단
          if (location.pathname !== '/state-stop') {
            checkAuth(false);
            dispatch(fetchWithcookieData());
          }
        }, 10000);
        */
      } catch (error) {
        console.error('인증 초기화 오류, 계속 진행합니다:', error);
        setAuthInitialized(true);
      }
    };
    
    initializeAuth();
  }, [dispatch, authInitialized, location.pathname, checkAuth]);

  // 주기적 확인 비활성화 - whoami 중복 호출 문제 해결을 위해 제거

  // 경로 변경 시 메뉴의 로그인 조건 확인 및 리다이렉트 처리
  useEffect(() => {
    // 이전 경로와 현재 경로가 같으면 중복 처리 방지
    if (prevPathRef.current === location.pathname) {
      return;
    }
    
    // 현재 경로 저장
    prevPathRef.current = location.pathname;
    
    // 에디터(/edit/) 경로나 state-stop 페이지에서는 리다이렉트 처리 중단
    if (location.pathname.startsWith('/edit/') || location.pathname === '/edit' || 
        location.pathname === '/state-stop') {
      return;
    }

    // 로그인 상태 확인
    const isLoggedIn = isUserLoggedIn || isAdminLoggedIn;
    
    // 현재 경로와 일치하는 메뉴 찾기
    const currentPath = location.pathname;
    
    // 모든 메뉴 목록 가져오기 (전역 편집기 메뉴 + 서버에서 가져온 메뉴)
    const allMenus = menus;
    
    // 현재 경로와 일치하는 메뉴 찾기 (정확한 경로 일치)
    let currentMenu = allMenus.find((menu: Menu) => menu.path === currentPath);
    
    // 가변 경로 처리 (예: product/:id) - 매치되지 않은 경우만 실행
    if (!currentMenu) {
      for (const menu of allMenus) {
        if (menu.path && menu.path.includes('/:')) {
          const menuPathPattern = menu.path;
          const menuPathParts = menuPathPattern.split('/').filter(Boolean);
          const currentPathParts = currentPath.split('/').filter(Boolean);
          
          // 빠른 길이 확인 - 길이가 다르면 계속 진행
          if (menuPathParts.length !== currentPathParts.length) {
            continue;
          }
          
          let isMatch = true;
          
          for (let i = 0; i < menuPathParts.length; i++) {
            const menuPart = menuPathParts[i];
            const currentPart = currentPathParts[i];
            
            if (menuPart.startsWith(':')) {
              // 매개변수 부분 - 계속 진행
              continue;
            } else if (menuPart !== currentPart) {
              // 일반 경로 부분 - 정확히 일치해야 함
              isMatch = false;
              break;
            }
          }
          
          if (isMatch) {
            currentMenu = menu as Menu;
            break;
          }
        }
      }
    }
    
    // 메뉴를 찾았고 로그인 조건이 있는 경우만 처리
    if (currentMenu) {
      // 로그아웃 시에만 표시(is_not_logged)가 설정된 메뉴에 로그인 상태로 접근한 경우
      if (currentMenu.is_not_logged && isLoggedIn) {
        navigate('/');
        return;
      }
      
      // 로그인 시에만 표시(is_logged)가 설정된 메뉴에 비로그인 상태로 접근한 경우
      if (currentMenu.is_logged && !isLoggedIn) {
        navigate('/');
        return;
      }
    }
  }, [location.pathname, isUserLoggedIn, isAdminLoggedIn, menus, navigate]);

  return <>{children}</>;
};

export default App;